import { SaleErrorCode } from '@/constants/saleConstraints';
import {
  SALE_ERROR_MESSAGES,
  formatInsufficientFundsMessage,
  hasFeeGap,
} from '@/constants/saleErrorMessages';

/**
 * Structured error thrown by the treasury-bill submit service calls. Despite the name, this parser is
 * shared by the sell AND purchase flows.
 *
 * The client-api endpoints return two distinct 400 surfaces:
 *  1. ProblemDetails — a machine-readable `code` extension (one of SaleErrorCode).
 *  2. ValidationProblemDetails — property-keyed `{ errors: { Amount: [...] } }`, no `code`.
 */
export interface SaleSubmitError extends Error {
  /** Present for the ProblemDetails surface — a canonical SaleErrorCode. */
  code?: SaleErrorCode;
  /** True for the ValidationProblemDetails (stateless validator) surface. */
  validation?: boolean;
  /**
   * The accrued-unpaid fee held back from the spendable balance, lifted off the 402
   * problem-details `feeUnavailableAmount` extension (#161 / feature preservoir-ai#117).
   *
   * This field exists because the INSUFFICIENT_FUNDS message is amount-PARAMETERISED and
   * `SALE_ERROR_MESSAGES` can only hold static strings — so the figure has to travel on the error
   * object to reach `mapSaleSubmitError`. Present only when the wire carried a usable positive
   * number; a zero (the legacy 2-arg server path) or non-numeric value is dropped here so it can
   * never be formatted into "£0.00" / "£NaN" downstream.
   */
  feeUnavailableAmount?: number;
}

const GENERIC_SALE_MESSAGE = 'Failed to submit treasury bill sell order';
const GENERIC_VALIDATION_MESSAGE = 'Validation error. Please check your input.';

/**
 * Recognised server error codes. This is a CLOSED allowlist — an unrecognised code silently degrades
 * to the generic fallback copy, so a new server code must be added here, to the `SaleErrorCode` union
 * and to `SALE_ERROR_MESSAGES`. This `Set` is the one registration point `tsc` CANNOT check: it is
 * typed `Set<string>` deliberately (it guards the untyped wire), so a code present in the union but
 * missing here compiles cleanly and degrades in production.
 */
const SALE_ERROR_CODES = new Set<string>([
  // Sell flow (shared-core SaleErrorCodes.cs).
  'BELOW_MIN_SALE',
  'NOT_STEP_MULTIPLE',
  'EXCEEDS_FACE_VALUE',
  'INVALID_REMAINDER',
  'INSUFFICIENT_HOLDING',
  'AMOUNT_NOT_POSITIVE',
  'PURCHASE_NOT_SETTLED',
  // Buy flow (client-api ClientPurchaseErrorCodes.cs).
  'PURCHASE_PRICE_BELOW_TOLERANCE',
  'PURCHASE_PRICE_ABOVE_TOLERANCE',
  'LISTING_UNAVAILABLE',
  // Net-of-fees funds guard (client-api ClientFundsErrorCodes.cs) — fires on BOTH the purchase and
  // withdrawal paths. Pinned character-for-character against the server constant.
  'INSUFFICIENT_FUNDS',
]);

/**
 * Minimal shape of the ProblemDetails body we care about (`code` and `feeUnavailableAmount` are
 * custom extensions, written verbatim by the server — System.Text.Json's camelCase policy does not
 * transform `ProblemDetails.Extensions` dictionary keys, so these literal key names are the wire
 * contract). Both are `unknown` because nothing validates the payload before we do.
 */
interface ProblemDetailsBody {
  code?: unknown;
  detail?: unknown;
  errors?: Record<string, unknown>;
  feeUnavailableAmount?: unknown;
}

function isSaleErrorCode(value: unknown): value is SaleErrorCode {
  return typeof value === 'string' && SALE_ERROR_CODES.has(value);
}

/**
 * Build a typed SaleSubmitError from a thrown Axios-style error.
 *
 * @param error           the caught error (typically an AxiosError)
 * @param genericMessage  fallback message for unrecognised / network errors
 */
export function parseSaleSubmitError(
  error: unknown,
  genericMessage: string = GENERIC_SALE_MESSAGE,
): SaleSubmitError {
  const data = (error as { response?: { data?: unknown } })?.response?.data as
    ProblemDetailsBody | undefined;

  // Surface 1 — ProblemDetails with a machine-readable `code` extension.
  if (data && isSaleErrorCode(data.code)) {
    const code = data.code;

    // Registration point 4a — lift the fee figure off the extension so the amount-parameterised
    // copy can be built here AND re-derived by `mapSaleSubmitError` from the caught error. Only a
    // usable positive number survives (see `hasFeeGap`): a zero or non-numeric value is dropped,
    // so the fallback path renders generic copy rather than "£0.00" / "£NaN".
    //
    // Scoped to INSUFFICIENT_FUNDS: the extension is only meaningful alongside that code, and the
    // sentence would be nonsense attached to, say, a price-tolerance rejection.
    const rawFee =
      typeof data.feeUnavailableAmount === 'number' ? data.feeUnavailableAmount : undefined;
    const feeUnavailableAmount =
      code === 'INSUFFICIENT_FUNDS' && hasFeeGap(rawFee) ? rawFee : undefined;

    const message =
      // The fee-gap sentence outranks the static map entry when there is a figure to name.
      (feeUnavailableAmount !== undefined
        ? formatInsufficientFundsMessage(feeUnavailableAmount)
        : undefined) ??
      SALE_ERROR_MESSAGES[code] ??
      (typeof data.detail === 'string' ? data.detail : undefined) ??
      genericMessage;

    const built = new Error(message) as SaleSubmitError;
    built.code = code;
    if (feeUnavailableAmount !== undefined) {
      built.feeUnavailableAmount = feeUnavailableAmount;
    }
    return built;
  }

  // Surface 2 — ValidationProblemDetails: property-keyed `errors`, no `code`.
  if (data && data.errors && typeof data.errors === 'object') {
    // Surface the first property message if available, else the generic copy.
    const firstKey = Object.keys(data.errors)[0];
    const firstVal = firstKey ? data.errors[firstKey] : undefined;
    const firstMessage =
      Array.isArray(firstVal) && typeof firstVal[0] === 'string' ? firstVal[0] : undefined;
    const built = new Error(firstMessage ?? GENERIC_VALIDATION_MESSAGE) as SaleSubmitError;
    built.validation = true;
    return built;
  }

  // Anything else / network — generic fallback.
  return new Error(genericMessage) as SaleSubmitError;
}

/**
 * Map a caught (already-parsed) SaleSubmitError to the user-facing string a component should render
 * in its submitError slot.
 *
 * @param error           the caught error, typically from parseSaleSubmitError
 * @param genericMessage  fallback for an uncoded / non-Error failure
 */
export function mapSaleSubmitError(
  error: unknown,
  genericMessage: string = GENERIC_SALE_MESSAGE,
): string {
  const e = error as SaleSubmitError | undefined;

  if (e && isSaleErrorCode(e.code)) {
    // Registration point 4b — the amount-parameterised branch. `SALE_ERROR_MESSAGES` is a
    // `Record<SaleErrorCode, string>` and cannot interpolate a figure, so INSUFFICIENT_FUNDS is
    // special-cased through the shared formatter when a fee gap travelled on the error. Without
    // this branch the code is "registered" everywhere `tsc` can see and STILL renders generic copy.
    if (e.code === 'INSUFFICIENT_FUNDS' && hasFeeGap(e.feeUnavailableAmount)) {
      return formatInsufficientFundsMessage(e.feeUnavailableAmount);
    }

    return SALE_ERROR_MESSAGES[e.code];
  }

  if (e?.validation) {
    return GENERIC_VALIDATION_MESSAGE;
  }

  if (error instanceof Error && error.message) {
    return error.message;
  }

  return genericMessage;
}
